import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import type { WaQuickReply } from "@/types/whatsapp";

/** Saved replies: type /shortcut in a chat to insert them */
export function QuickRepliesManager() {
  const { success, error } = useToast();
  const { user } = useAuth();
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const supervisor = roleCode === "SUPER_ADMIN" || roleCode === "SALES_HEAD";
  const [rows, setRows] = useState<WaQuickReply[]>([]);
  const [editing, setEditing] = useState<WaQuickReply | null>(null);
  const [shortcut, setShortcut] = useState("");
  const [body, setBody] = useState("");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/api/WhatsApp/QuickReplies")
      .then((res) => setRows(unwrapArray<WaQuickReply>(res)))
      .catch(() => setRows([]));

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditing(null);
    setShortcut("");
    setBody("");
    setShared(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { shortcut: shortcut.trim().replace(/^\//, ""), body, shared };
      if (editing) await api.put(`/api/WhatsApp/QuickReplies/${editing.id}`, payload);
      else await api.post("/api/WhatsApp/QuickReplies", payload);
      success("Quick reply saved");
      reset();
      load();
    } catch (err: any) {
      error("Could not save", err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (q: WaQuickReply) => {
    try {
      await api.delete(`/api/WhatsApp/QuickReplies/${q.id}`);
      load();
    } catch (err: any) {
      error("Could not delete", err.message);
    }
  };

  const canEdit = (q: WaQuickReply) => (q.user_id ? q.user_id === user?.Id : supervisor);

  return (
    <div className="grid gap-4 lg:grid-cols-[380px_1fr] max-w-5xl">
      <form onSubmit={save} className="bg-card border rounded-2xl p-5 space-y-3 h-fit">
        <h2 className="text-sm font-bold">{editing ? "Edit quick reply" : "New quick reply"}</h2>
        <div className="space-y-1">
          <Label className="text-xs">Shortcut</Label>
          <Input required value={shortcut} onChange={(e) => setShortcut(e.target.value)} placeholder="price" />
          <p className="text-[11px] text-muted-foreground">Type /{shortcut || "price"} in a chat to insert it.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Message</Label>
          <Textarea required rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        {supervisor && (
          <div className="flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-xs">Share with the whole team</span>
            <Switch checked={shared} onCheckedChange={setShared} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          {editing && (
            <Button type="button" variant="outline" onClick={reset}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin mr-1.5" />}
            Save
          </Button>
        </div>
      </form>

      <div className="bg-card border rounded-2xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No quick replies yet.</div>
        ) : (
          <ul className="divide-y">
            {rows.map((q) => (
              <li key={q.id} className="px-5 py-3 flex gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-primary">/{q.shortcut}</span>
                    {!q.user_id && <Badge variant="blue" className="text-[10px]">Team</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-0.5 line-clamp-3">{q.body}</p>
                </div>
                {canEdit(q) && (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      aria-label="Edit"
                      onClick={() => (setEditing(q), setShortcut(q.shortcut), setBody(q.body), setShared(!q.user_id))}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" aria-label="Delete" onClick={() => remove(q)}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
